import { useEffect, useRef, useState } from "react";
import { loadAttachment, resolvedAttachmentToFile } from "@/screen/Identities/services/identityAttachmentsService";
import { isAttachmentLoadError } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import type { ResolvedAttachment } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";

export type AttachmentPreviewStatus = "idle" | "loading" | "ready" | "error";

export interface IdentityAttachmentPreviewState
{
    status: AttachmentPreviewStatus;
    objectUrl: string | null;
    mimeType: string | null;
    fileName: string | null;
    /** Mensaje de error escalable al componente padre */
    error: string | null;
}

export interface UseIdentityAttachmentPreviewReturn extends IdentityAttachmentPreviewState
{
    resolvedFile: File | null;
}

function resolveAttachmentErrorMessage(err: unknown, documentLabel: string): string
{
    const label = `"${documentLabel}"`;

    if(isAttachmentLoadError(err))
    {
        if(err.httpStatus !== null && err.httpStatus >= 500)
        {
            return `Se ha producido un error en el servidor al recuperar ${label}. Inténtalo de nuevo más tarde. Si el problema persiste, pulsa el botón de '¿Necesitas asistencia?'.`;
        }

        if(err.httpStatus === 404)
        {
            return `No se ha encontrado el archivo ${label}.`;
        }

        if(err.httpStatus !== null)
        {
            return `No se ha podido recuperar ${label} (código ${err.httpStatus}).`;
        }

        return `No se ha podido recuperar ${label}. Comprueba tu conexión e inténtalo de nuevo. Si el problema persiste, pulsa el botón de '¿Necesitas asistencia?'.`;
    }

    return "No se ha podido cargar el adjunto. Comprueba tu conexión e inténtalo de nuevo. Si el problema persiste, pulsa el botón de '¿Necesitas asistencia?'.";
}

const IDLE_STATE: IdentityAttachmentPreviewState =
{
    status: "idle",
    objectUrl: null,
    mimeType: null,
    fileName: null,
    error: null,
};

/**
 * Resuelve `source` (File local o nombre/URL remota) a una URL de objeto para preview.
 *
 * - Si `source` es un `File`   → crea objectUrl localmente (sin red).
 * - Si `source` es un `string` → descarga el blob UNA sola vez y crea objectUrl.
 * - Si `source` es `null`      → estado idle.
 *
 * `documentLabel` es el nombre descriptivo del documento ("Anverso DNI", etc.) que se
 * incluye en los mensajes de error.
 *
 * Gestiona revocación automática de objectUrls al desmontar o cambiar de source.
 */
export function useIdentityAttachmentPreview(source: File | string | null, documentLabel: string): UseIdentityAttachmentPreviewReturn
{
    const [previewState, setPreviewState] = useState<IdentityAttachmentPreviewState>(IDLE_STATE);
    const [resolvedFile, setResolvedFile] = useState<File | null>(null);

    const documentLabelRef = useRef(documentLabel);
    documentLabelRef.current = documentLabel;

    useEffect(() =>
    {
        if(source === null || source === undefined)
        {
            setPreviewState(IDLE_STATE);
            setResolvedFile(null);
            return;
        }

        let cancelled = false;
        let localObjectUrl: string | null = null;

        const cleanup = () =>
        {
            cancelled = true;
            if(localObjectUrl)
            {
                URL.revokeObjectURL(localObjectUrl);
            }
        };

        if(source instanceof File)
        {
            localObjectUrl = URL.createObjectURL(source);
            setPreviewState(
            {
                status: "ready",
                objectUrl: localObjectUrl,
                mimeType: source.type || null,
                fileName: source.name,
                error: null,
            });
            setResolvedFile(null);
            return cleanup;
        }

        // URL blob: ya resuelta localmente (p.ej. por useDocumentUpload): se usa directamente
        if(source.startsWith("blob:"))
        {
            setPreviewState(
            {
                status: "ready",
                objectUrl: source,
                mimeType: null,
                fileName: null,
                error: null,
            });
            setResolvedFile(null);
            // No registramos localObjectUrl porque la revocación es responsabilidad del creador
            return () => {cancelled = true};
        }

        // Source es string remoto: descarga única
        setPreviewState((prev) => ({...prev, status: "loading", error: null}));
        setResolvedFile(null);

        loadAttachment(source).then((resolved: ResolvedAttachment) =>
        {
            if(cancelled)
            {
                URL.revokeObjectURL(resolved.objectUrl);
                return;
            }
            localObjectUrl = resolved.objectUrl;
            setPreviewState(
            {
                status: "ready",
                objectUrl: resolved.objectUrl,
                mimeType: resolved.mimeType,
                fileName: resolved.fileName,
                error: null,
            });
            setResolvedFile(resolvedAttachmentToFile(resolved));
        })
        .catch((err: unknown) =>
        {
            if(cancelled)
            {
                return;
            }
            const message = resolveAttachmentErrorMessage(err, documentLabelRef.current);
            setPreviewState(
            {
                status: "error",
                objectUrl: null,
                mimeType: null,
                fileName: null,
                error: message,
            });
            setResolvedFile(null);
        });

        return cleanup;
    }, [source]);

    return {...previewState, resolvedFile};
}